import crypto from "crypto";
import { EFAWATEERCOM_CONVENTION } from "@sajilni/config";

/**
 * Returns an unsigned 32-bit random integer.
 *
 * Strategy (in order):
 * - Use Web Crypto API when available (browser or Node's webcrypto)
 * - Fallback to Node's crypto.randomInt for secure randomness
 * - Final fallback: Math.random (non-cryptographic)
 */
const getRandomUint32 = (): number => {
  if (crypto.getRandomValues) {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return arr[0]!;
  }

  if (crypto.randomInt) {
    // Range: 0 .. 2^32-1
    return crypto.randomInt(0, 0x1_0000_0000);
  }

  // Non-cryptographic fallback
  return Math.floor(Math.random() * 0x1_0000_0000);
};

/**
 * Generates a unique 7-digit string for use in EFawateerCom bill numbers.
 *
 * How it works:
 * 1. Takes current timestamp modulo 10M to get a time-based component
 * 2. Gets an unsigned 32-bit random integer (secure source when available)
 * 3. Combines timestamp and random value using XOR for unpredictability
 * 4. Coerces the XOR result to unsigned (>>> 0) BEFORE modulo to avoid negatives
 * 5. Pads to exactly 7 digits with leading zeros
 *
 * The unsigned coercion ensures no negative values appear in logs or output.
 */
const generateSevenDigitSuffix = (): string => {
  const nowMod = Date.now() % 10_000_000; // 0..9_999_999
  const rand = getRandomUint32(); // 0..2^32-1
  // Ensure unsigned before modulo to avoid negative values
  const mixed = ((rand ^ nowMod) >>> 0) % 10_000_000; // 0..9_999_999
  return String(mixed).padStart(7, "0");
};

/**
 * Generates EFawateerCom BillPull number in the format:
 * 2 + 3-digit receiver code + 7-digit suffix (no hyphen or negative values).
 */
export const generateEFawateerComBillPull = (options?: {
  receiverCode?: number;
}): string => {
  const receiverCode =
    options?.receiverCode ?? EFAWATEERCOM_CONVENTION.SAJILNI.RECEIVER_CODE;
  const prefix = `2${String(receiverCode).padStart(3, "0")}`;
  const suffix = generateSevenDigitSuffix();
  return `${prefix}${suffix}`;
};
